/**
 * Gestionnaire de cache mémoire local pour les données de configuration
 * Supporte le cache avec TTL, la gestion des requêtes concurrentes et le nettoyage sélectif
 */

import debug from '@commons-shared/debug.js';
import { config } from '../config/environment.js';
import { generateCacheKey } from '@commons-shared/cache-key-generator.js';

// Configuration du cache
const CACHE_CONFIG = {
  // TTL par défaut pour les données de configuration
  defaultTTL: 6 * 60 * 60 * 1000, // 6 heures
  // Patterns des endpoints à mettre en cache
  cacheablePatterns: ['/api/config/read/','/api/wa/enablecache/']
};

class CacheManager {
  constructor() {
    this.cache = new Map();
    this.loadingRequests = new Map(); // Pour gérer les requêtes concurrentes
    this.stats = {
      hits: 0,
      misses: 0,
      sets: 0,
      clears: 0
    };
  }

  /**
   * Vérifie si un endpoint doit être mis en cache
   * @param {string} endpoint - L'endpoint de l'API
   * @returns {boolean} True si l'endpoint doit être mis en cache
   */
  shouldCache(endpoint) {
    return CACHE_CONFIG.cacheablePatterns.some(pattern => endpoint.startsWith(pattern));
  }

  /**
   * Détermine le TTL par défaut pour le cache
   * @returns {number} TTL en millisecondes
   */
  getDefaultTTL() {
    return CACHE_CONFIG.defaultTTL;
  }

  /**
   * Génère une clé de cache pour un endpoint avec ses paramètres
   * @param {string} endpoint - L'endpoint de l'API
   * @param {Object} options - Options de la requête (pour inclure les query params)
   * @returns {string} Clé de cache
   */
  generateCacheKey(endpoint, options = {}) {
    return generateCacheKey(endpoint, options);
  }

  /**
   * Vérifie si une donnée en cache est encore valide
   * @param {Object} cacheEntry - Entrée du cache
   * @returns {boolean} True si valide, false si expirée
   */
  isValid(cacheEntry) {
    if (!cacheEntry) return false;
    return Date.now() < cacheEntry.expiry;
  }

  /**
   * Récupère une donnée du cache
   * @param {string} cacheKey - Clé de cache
   * @returns {any|null} Données mises en cache ou null
   */
  get(cacheKey) {
    const entry = this.cache.get(cacheKey);
    
    if (!entry) {
      this.stats.misses++;
      debug.log(config, `Cache MISS: ${cacheKey}`);
      return null;
    }
    
    if (!this.isValid(entry)) {
      this.cache.delete(cacheKey);
      this.stats.misses++;
      debug.log(config, `Cache EXPIRED: ${cacheKey}`);
      return null;
    }
    
    this.stats.hits++;
    debug.log(config, `Cache HIT: ${cacheKey}`);
    return entry.data;
  }

  /**
   * Met une donnée en cache
   * @param {string} cacheKey - Clé de cache
   * @param {any} data - Données à mettre en cache
   * @param {number} ttl - Durée de vie en millisecondes
   * @param {string} endpoint - Endpoint original pour référence
   */
  set(cacheKey, data, ttl, endpoint = null) {
    const entry = {
      data,
      created: Date.now(),
      expiry: Date.now() + ttl,
      endpoint
    };
    
    this.cache.set(cacheKey, entry);
    this.stats.sets++;
    debug.log(config, `Cache SET: ${cacheKey} (TTL: ${ttl}ms)`);
  }

  /**
   * Supprime une entrée spécifique du cache
   * @param {string} cacheKey - Clé de cache à supprimer
   */
  delete(cacheKey) {
    const deleted = this.cache.delete(cacheKey);
    if (deleted) {
      debug.log(config, `Cache DELETE: ${cacheKey}`);
    }
    return deleted;
  }

  /**
   * Vide tout le cache correspondant à un pattern de clé
   * @param {string} keyPattern - Pattern de clé à vider
   * @returns {number} Nombre d'entrées supprimées
   */
  clearByKeyPattern(keyPattern) {
    let deletedCount = 0;
    
    for (const [cacheKey] of this.cache) {
      if (cacheKey.includes(keyPattern)) {
        this.cache.delete(cacheKey);
        deletedCount++;
      }
    }
    
    this.stats.clears++;
    debug.log(config, `Cache CLEAR PATTERN: ${keyPattern} (${deletedCount} entrées supprimées)`);
    return deletedCount;
  }

  /**
   * Vide tout le cache
   * @returns {number} Nombre d'entrées supprimées
   */
  clearAll() {
    const size = this.cache.size;
    this.cache.clear();
    this.loadingRequests.clear();
    this.stats.clears++;
    debug.log(config, `Cache CLEAR ALL: ${size} entrées supprimées`);
    return size;
  }

  /**
   * Obtient le statut détaillé du cache
   * @returns {Object} Statistiques et contenu du cache
   */
  getStatus() {
    const entries = [];
    
    // Analyser le contenu du cache
    for (const [cacheKey, entry] of this.cache) {
      const isValid = this.isValid(entry);
      
      entries.push({
        key: cacheKey,
        timestamp: entry.timestamp,
        expiry: entry.expiry,
        isValid,
        size: JSON.stringify(entry.data).length,
        data: entry.data // Inclure les données pour la visualisation
      });
    }
    
    return {
      stats: { ...this.stats },
      entries,
      totalEntries: this.cache.size,
      loadingRequests: this.loadingRequests.size
    };
  }

  /**
   * Gère les requêtes concurrentes pour éviter les doublons
   * @param {string} cacheKey - Clé de cache
   * @param {Function} fetchFunction - Fonction pour récupérer les données
   * @returns {Promise<any>} Données récupérées
   */
  async handleConcurrentRequest(cacheKey, fetchFunction) {
    // Vérifier si une requête est déjà en cours pour cette clé
    if (this.loadingRequests.has(cacheKey)) {
      debug.log(config, `Requête concurrente détectée pour ${cacheKey}, attente de la requête existante`);
      return await this.loadingRequests.get(cacheKey);
    }
    
    // Créer et stocker la promesse de chargement
    const loadingPromise = fetchFunction();
    this.loadingRequests.set(cacheKey, loadingPromise);
    
    try {
      const result = await loadingPromise;
      return result;
    } finally {
      // Nettoyer la requête de la liste des chargements en cours
      this.loadingRequests.delete(cacheKey);
    }
  }

  /**
   * Obtient la liste des clés de cache disponibles
   * @returns {Array} Liste des clés de cache
   */
  getAvailableKeys() {
    return Array.from(this.cache.keys());
  }

  /**
   * Obtient toutes les données du cache avec leurs métadonnées
   * @returns {Object} Structure complète du cache
   */
  getAllCacheData() {
    const cacheData = {};
    
    for (const [key, entry] of this.cache.entries()) {
      cacheData[key] = {
        data: entry.data,
        metadata: {
          created: new Date(entry.created).toISOString(),
          expiry: new Date(entry.expiry).toISOString(),
          isValid: this.isValid(entry),
          ttl: entry.expiry - Date.now(),
          size: JSON.stringify(entry.data).length,
          endpoint: entry.endpoint || 'unknown'
        }
      };
    }
    
    return cacheData;
  }
}

// Instance singleton du gestionnaire de cache
export const cacheManager = new CacheManager();

// Export des constantes pour usage externe
export { CACHE_CONFIG };

// Fonction utilitaire pour obtenir les clés de cache disponibles
export function getCacheKeys() {
  return cacheManager.getAvailableKeys();
}

// Fonction utilitaire pour obtenir toutes les données du cache
export function getAllCacheData() {
  return cacheManager.getAllCacheData();
}

// Fonction utilitaire pour vider le cache par pattern de clé
export function clearCacheByKeyPattern(keyPattern) {
  return cacheManager.clearByKeyPattern(keyPattern);
}

// Fonction utilitaire pour obtenir le statut du cache
export function getCacheStatus() {
  return cacheManager.getStatus();
}

// Fonction utilitaire pour vérifier si un endpoint doit être mis en cache
export function shouldCacheEndpoint(endpoint) {
  return cacheManager.shouldCache(endpoint);
}

/**
 * Ajoute un nouveau pattern d'endpoint à mettre en cache
 * @param {string} pattern - Le pattern d'endpoint à ajouter (ex: '/api/ia/models/')
 * @returns {boolean} - True si le pattern a été ajouté, false s'il existait déjà
 */
export function addCacheablePattern(pattern) {
  // Vérifier si le pattern existe déjà
  if (CACHE_CONFIG.cacheablePatterns.includes(pattern)) {
    debug.log(config, `Pattern de cache déjà existant: ${pattern}`);
    return false;
  }
  
  // Ajouter le nouveau pattern
  CACHE_CONFIG.cacheablePatterns.push(pattern);
  debug.log(config, `Nouveau pattern de cache ajouté: ${pattern}`);
  return true;
}

/**
 * Récupère la liste des patterns d'endpoints mis en cache
 * @returns {Array<string>} - Liste des patterns
 */
export function getCacheablePatterns() {
  return [...CACHE_CONFIG.cacheablePatterns];
}
